import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useWorkspace } from '@/features/Workspace/lib/useWorkspace'
import { parseUsersInDepartment } from '@/shared/lib/users'
import { useDashboard } from '../Dashboard/lib/useDashboard'
import dashboardApi from '../Dashboard/api/dashboard.api'
import { DealAnalyticsPanel } from '../Dashboard/components/cards/DepartmentsPlan/DealAnalyticsPanel'
import DealsDetailsView from '../Dashboard/components/cards/DepartmentsPlan/DealsDetailsView'
import {
  DealsAnalyticsContext,
  DepartmentPlanDeal,
  MainTypesPlans,
  MyPlanResultInterface,
} from '../Dashboard/model/dashboard.types'
import css from './SalesAnalyticsPage.module.css'

type AnalyticsScope = 'company' | 'department' | 'employee'
type LoadedAnalytics = { deals: DepartmentPlanDeal[]; context: DealsAnalyticsContext; performerIds?: number[] }

function errorText(error: unknown): string {
  const candidate = error as { response?: { data?: { error_message?: unknown } }; message?: unknown }
  if (typeof candidate.response?.data?.error_message === 'string') return candidate.response.data.error_message
  if (typeof candidate.message === 'string') return candidate.message
  return 'Не удалось загрузить аналитику'
}

export default function SalesAnalyticsPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const { departments, users } = useWorkspace()
  const { dateStart: dashboardDateStart, dateEnd: dashboardDateEnd } = useDashboard()
  const scope = (params.get('scope') || 'company') as AnalyticsScope
  const departmentId = Number(params.get('departmentId') || 0) || null
  const userId = Number(params.get('userId') || 0) || null
  const dateStart = dashboardDateStart || params.get('dateStart') || ''
  const dateEnd = dashboardDateEnd || params.get('dateEnd') || ''
  const [loaded, setLoaded] = useState<LoadedAnalytics | null>(null)
  const [knownPerformerIds, setKnownPerformerIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dealsOpen, setDealsOpen] = useState(false)

  const department = departments.find(item => item.id === departmentId)
  const employee = users.find(item => item.id === userId)
  const employeeLabel = employee ? `${employee.last_name} ${employee.name}` : (userId ? `Сотрудник ${userId}` : '')
  const employees = useMemo(
    () => {
      const actualPerformerIds = new Set([
        ...knownPerformerIds,
        ...(loaded?.performerIds ?? []),
        ...(loaded?.deals ?? []).map(deal => Number(deal.assigned_by_id)),
      ])
      const byDepartment = parseUsersInDepartment(users, departmentId)
      const performers = users.filter(item => actualPerformerIds.has(Number(item.id)))
      const unique = Array.from(new Map([...byDepartment, ...performers].map(item => [item.id, item])).values())
      return unique.sort((a, b) => {
      if (a.id === department?.uf_head) return -1
      if (b.id === department?.uf_head) return 1
      return `${a.last_name} ${a.name}`.localeCompare(`${b.last_name} ${b.name}`, 'ru')
      })
    },
    [users, departmentId, department?.uf_head, loaded?.deals, loaded?.performerIds, knownPerformerIds]
  )

  useEffect(() => setKnownPerformerIds([]), [departmentId])
  useEffect(() => setDealsOpen(false), [scope, departmentId, userId, dateStart, dateEnd])

  useEffect(() => {
    if (!dashboardDateStart || !dashboardDateEnd ||
      (params.get('dateStart') === dashboardDateStart && params.get('dateEnd') === dashboardDateEnd)) return
    const next = new URLSearchParams(params)
    next.set('dateStart', dashboardDateStart)
    next.set('dateEnd', dashboardDateEnd)
    setParams(next, { replace: true })
  }, [dashboardDateStart, dashboardDateEnd, params, setParams])

  useEffect(() => {
    if (!loaded) return
    const ids = loaded.deals.map(deal => Number(deal.assigned_by_id)).filter(Boolean)
    setKnownPerformerIds(current => Array.from(new Set([...current, ...ids])))
  }, [loaded])

  useEffect(() => {
    let active = true
    if (!dateStart || !dateEnd || (scope !== 'company' && !departmentId)) {
      setError('Не указан период или отдел для анализа')
      setLoading(false)
      return () => { active = false }
    }
    setLoading(true)
    setError(null)
    setLoaded(null)

    const load = async (): Promise<LoadedAnalytics> => {
      if (scope === 'company') {
        const [opportunity, profit, deals] = await Promise.all([
          dashboardApi.getCompanyPlanResult({ dateStart, dateEnd, type: MainTypesPlans.opportunitie, isPeriod: true }),
          dashboardApi.getCompanyPlanResult({ dateStart, dateEnd, type: MainTypesPlans.profit, isPeriod: true }),
          dashboardApi.getCompanyDeals({ dateStart, dateEnd }),
        ])
        return {
          deals: deals.data,
          context: {
            dateStart, dateEnd, scopeLabel: 'Вся компания', scopeKind: 'company',
            opportunityTarget: opportunity.data.target ?? 0,
            profitTarget: profit.data.target ?? 0,
            opportunityPreparation: opportunity.data.valuePreparation ?? 0,
            profitPreparation: profit.data.valuePreparation ?? 0,
            plannedSalesOpportunity: opportunity.data.plannedSalesOpportunity ?? 0,
            plannedSalesProfit: profit.data.plannedSalesProfit ?? 0,
          },
        }
      }

      if (scope === 'employee' && userId && departmentId) {
        const [response, departmentDeals] = await Promise.all([
          dashboardApi.getUserPlanResult({ userId, departmentId, dateStart, dateEnd, isPeriod: true }),
          dashboardApi.getDepartmentsResult({ dateStart, dateEnd, isPeriod: true, type: MainTypesPlans.opportunitie, departmentId }),
        ])
        const result: MyPlanResultInterface = response.data
        return {
          deals: result.deals ?? [],
          performerIds: (departmentDeals.data.deals ?? []).map(deal => Number(deal.assigned_by_id)),
          context: {
            dateStart, dateEnd, departmentId, userId, scopeKind: 'employee',
            scopeLabel: employeeLabel,
            opportunityTarget: result.opportunity?.target ?? 0,
            profitTarget: result.profit?.target ?? 0,
            opportunityPreparation: result.opportunity?.valuePreparation ?? 0,
            profitPreparation: result.profit?.valuePreparation ?? 0,
            plannedSalesOpportunity: result.plannedSalesOpportunity ?? 0,
            plannedSalesProfit: result.plannedSalesProfit ?? 0,
          },
        }
      }

      const [opportunity, profit] = await Promise.all([
        dashboardApi.getDepartmentsResult({ dateStart, dateEnd, isPeriod: true, type: MainTypesPlans.opportunitie, departmentId: departmentId! }),
        dashboardApi.getDepartmentsResult({ dateStart, dateEnd, isPeriod: true, type: MainTypesPlans.profit, departmentId: departmentId! }),
      ])
      return {
        deals: opportunity.data.deals ?? profit.data.deals ?? [],
        performerIds: (opportunity.data.deals ?? profit.data.deals ?? []).map(deal => Number(deal.assigned_by_id)),
        context: {
          dateStart, dateEnd, departmentId: departmentId!, scopeKind: 'department',
          scopeLabel: department?.name ?? `Отдел ${departmentId}`,
          opportunityTarget: opportunity.data.target ?? 0,
          profitTarget: profit.data.target ?? 0,
          opportunityPreparation: opportunity.data.valuePreparation ?? 0,
          profitPreparation: profit.data.valuePreparation ?? 0,
          plannedSalesOpportunity: opportunity.data.plannedSalesOpportunity ?? 0,
          plannedSalesProfit: profit.data.plannedSalesProfit ?? 0,
        },
      }
    }

    load().then(result => { if (active) setLoaded(result) })
      .catch(requestError => { if (active) setError(errorText(requestError)) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [scope, departmentId, userId, dateStart, dateEnd, department?.name, employeeLabel])

  const selectPerformer = (value: string) => {
    const next = new URLSearchParams(params)
    if (value) {
      next.set('scope', 'employee')
      next.set('userId', value)
    } else {
      next.set('scope', 'department')
      next.delete('userId')
    }
    setParams(next)
  }

  return <>
    <div className={css.pageHeader}>
      <button type="button" className={css.back} onClick={() => navigate('/SalePlan')}>← К планам продаж</button>
      <div className={css.titleRow}>
        <div>
          <span className={css.eyebrow}>Управленческая аналитика</span>
          <h1>{loaded?.context.scopeLabel ?? 'Аналитика продаж'}</h1>
        </div>
        {scope !== 'company' && departmentId && <label className={css.performer}>
          <span>Исполнитель плана</span>
          <select value={scope === 'employee' ? String(userId ?? '') : ''} onChange={event => selectPerformer(event.target.value)}>
            <option value="">Весь отдел</option>
            {employees.map(item => <option key={item.id} value={item.id}>
              {item.last_name} {item.name}{item.id === department?.uf_head ? ' — руководитель' : ''}
            </option>)}
          </select>
        </label>}
      </div>
    </div>

    {loading && <div className={css.state}>Собираем аналитику…</div>}
    {error && <div className={`${css.state} ${css.error}`}>{error}</div>}
    {loaded && <main className={css.content}>
      <DealAnalyticsPanel deals={loaded.deals} context={loaded.context} />
      <section className={css.dealsSection}>
        <div className={css.sectionHeading}>
          <div><span>Детализация</span><h2>Закрытые сделки <small>{loaded.deals.length}</small></h2></div>
          <div className={css.detailsActions}>
            <button type="button" onClick={() => setDealsOpen(value => !value)}>
              {dealsOpen ? 'Скрыть сделки' : 'Показать сделки'}
            </button>
          </div>
        </div>
        {dealsOpen && <div className={css.dealsBody}>
          <DealsDetailsView loading={false} deals={loaded.deals} exportTitle={`${loaded.context.scopeLabel} — сделки`} />
        </div>}
      </section>
    </main>}
  </>
}
